import type {
  AssignmentRule,
  AssignmentRuleMeta,
  AuditEvent,
  BlockDefinition,
  BlockDefinitionMeta,
  BlockVersionMeta,
  ImageAsset,
  LoadedResource,
  Page,
  PrinterProfile,
  PrinterProfileMeta,
  ResourceContract,
  SaveResult,
  Template,
  TemplateMeta,
} from '@tsagko/label-contracts'
import type { EmbeddedTransport } from './transport'

/**
 * Embedded `ResourceContract` — marshals every method to the Odoo host via
 * `transport.request(method, payload, { signal })`. Payload keys are the
 * named args documented in the interface sheet's bridge method table; the
 * host's `anb.label.bridge.dispatch(method, payload)` is the single entry
 * point these calls arrive at on the other side of the postMessage bridge.
 *
 * Response envelopes are already `Result<...>`-shaped by the host — this
 * layer is a pure pass-through, it does not re-wrap or validate them
 * (unlike `getSessionContext` in `./operations.ts`, which does validate).
 */
export function createResourceContract(transport: EmbeddedTransport): ResourceContract {
  return {
    listTemplates: (query, signal) =>
      transport.request<Page<TemplateMeta>>('listTemplates', { query }, { signal }),
    loadTemplate: (id, signal) =>
      transport.request<LoadedResource<Template>>('loadTemplate', { id }, { signal }),
    saveTemplate: (resource, options, signal) =>
      transport.request<SaveResult<TemplateMeta>>('saveTemplate', { resource, options }, { signal }),
    deleteTemplate: (id, options, signal) =>
      transport.request<void>('deleteTemplate', { id, options }, { signal }),

    listBlocks: (query, signal) =>
      transport.request<Page<BlockDefinitionMeta>>('listBlocks', { query }, { signal }),
    loadBlock: (id, version, signal) =>
      transport.request<LoadedResource<BlockDefinition>>('loadBlock', { id, version }, { signal }),
    saveBlock: (resource, options, signal) =>
      transport.request<SaveResult<BlockDefinitionMeta>>('saveBlock', { resource, options }, { signal }),
    saveBlockDraft: (id, body, options, signal) =>
      transport.request<SaveResult<{ kind: 'draft' }>>('saveBlockDraft', { id, body, options }, { signal }),
    deleteBlockDraft: (id, options, signal) =>
      transport.request<void>('deleteBlockDraft', { id, options }, { signal }),
    promoteBlockDraft: (id, body, options, signal) =>
      transport.request<{ version: number; etag: string }>('promoteBlockDraft', { id, body, options }, { signal }),
    listBlockVersions: (id, signal) =>
      transport.request<BlockVersionMeta[]>('listBlockVersions', { id }, { signal }),
    listTemplatesUsingBlock: (blockId, version, signal) =>
      transport.request<TemplateMeta[]>('listTemplatesUsingBlock', { blockId, version }, { signal }),
    deleteBlock: (id, options, signal) =>
      transport.request<void>('deleteBlock', { id, options }, { signal }),

    listRules: (query, signal) =>
      transport.request<Page<AssignmentRuleMeta>>('listRules', { query }, { signal }),
    loadRule: (id, signal) =>
      transport.request<LoadedResource<AssignmentRule>>('loadRule', { id }, { signal }),
    saveRule: (resource, options, signal) =>
      transport.request<SaveResult<AssignmentRuleMeta>>('saveRule', { resource, options }, { signal }),
    deleteRule: (id, options, signal) =>
      transport.request<void>('deleteRule', { id, options }, { signal }),

    listPrinterProfiles: (query, signal) =>
      transport.request<Page<PrinterProfileMeta>>('listPrinterProfiles', { query }, { signal }),
    loadPrinterProfile: (id, signal) =>
      transport.request<LoadedResource<PrinterProfile>>('loadPrinterProfile', { id }, { signal }),
    savePrinterProfile: (resource, options, signal) =>
      transport.request<SaveResult<PrinterProfileMeta>>('savePrinterProfile', { resource, options }, { signal }),
    deletePrinterProfile: (id, options, signal) =>
      transport.request<void>('deletePrinterProfile', { id, options }, { signal }),

    listImageAssets: (query, signal) =>
      transport.request<Page<ImageAsset>>('listImageAssets', { query }, { signal }),
    loadImageAsset: (id, signal) =>
      transport.request<LoadedResource<ImageAsset>>('loadImageAsset', { id }, { signal }),
    saveImageAsset: (asset, options, signal) =>
      transport.request<SaveResult<ImageAsset>>('saveImageAsset', { resource: asset, options }, { signal }),
    deleteImageAsset: (id, options, signal) =>
      transport.request<void>('deleteImageAsset', { id, options }, { signal }),

    listAuditEvents: (query, signal) =>
      transport.request<Page<AuditEvent>>('listAuditEvents', { query }, { signal }),
  }
}
